/**
 * DataContext — localStorage-backed single source of truth for all prototype data.
 *
 * Usage:
 *   const { bills, purchaseOrders, ... } = useData();
 *
 * Mutations persist to localStorage immediately and trigger re-renders via context.
 * "Reset Data" wipes localStorage and reloads the page with seed defaults.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Supplier, Product, PurchaseOrder, POLineDetail, Bill, BillLineItem,
  Statement, StatementStatus, Site, Project, Job, SupplierCodeMap,
  SupplierPriceBookEntry, Quote, QuoteStatus, QuoteLineDetail,
  Invoice, InvoiceStatus, InvoiceLineDetail, InvoicePayment, BillStatus,
  PoStatus,
} from '../data/types';
import {
  DEFAULT_SUPPLIERS, DEFAULT_PRODUCTS, DEFAULT_PURCHASE_ORDERS,
  DEFAULT_PO_LINE_DETAILS, DEFAULT_BILLS, DEFAULT_BILL_LINE_ITEMS,
  DEFAULT_SITES, DEFAULT_PROJECTS,
  DEFAULT_STATEMENTS, DEFAULT_JOBS,
  DEFAULT_QUOTES, DEFAULT_QUOTE_LINE_DETAILS,
  DEFAULT_INVOICES, DEFAULT_INVOICE_LINE_DETAILS, DEFAULT_INVOICE_PAYMENTS,
} from '../data/seedData';

// v1.12.0 — bumped storage key to v4 so existing users pick up the new
// Sites + Projects collections on next load. Their bills / POs / statements
// are merged forward via the loadState() defaults-merge below — no data
// loss.
const STORAGE_KEY = 'fi_proto_data_v7';

// ── State shape ───────────────────────────────────────────────────────────────
interface AppState {
  suppliers: Supplier[];
  products: Product[];
  purchaseOrders: PurchaseOrder[];
  poLineDetails: POLineDetail[];
  bills: Bill[];
  billLineItems: Record<string, BillLineItem[]>;
  statements: Statement[];
  sites: Site[];
  projects: Project[];
  jobs: Job[];
  // v1.29 — sell-side quotes + their line details (parallel to PO + poLineDetails).
  quotes: Quote[];
  quoteLineDetails: QuoteLineDetail[];
  // v1.30 — customer invoices (AR) + line details.
  invoices: Invoice[];
  invoiceLineDetails: InvoiceLineDetail[];
  invoicePayments: InvoicePayment[];
  // v1.17 — persisted supplier-code -> internal-code reconciliation memory.
  // Starts empty; grows only when a user reconciles a code.
  supplierCodeMaps: SupplierCodeMap[];
  // v1.18 — per-supplier price book entries (managed supplier codes). Starts
  // empty; grows when a quote is imported or a code is added manually.
  supplierPriceBook: SupplierPriceBookEntry[];
  // v1.20 — progressive disclosure: when off, supplier-code UI is hidden but
  // aliases are still captured silently in the price book.
  trackSupplierCodes: boolean;
  // v1.52.15 — 3-letter company prefix for auto-minted inventory codes (FIE-234).
  internalCodePrefix: string;
  // v1.46 — when on, an imported bill auto-links to a PO / job / project matched
  // from its reference field without asking. When off, we offer the match instead.
  autoLinkBillReference: boolean;
  // v1.48 — accounting product approved bills are pushed to for payment.
  accountingProduct: AccountingProduct;
}

export type AccountingProduct = 'Xero' | 'MYOB' | 'QuickBooks';

// ── Context value (state + mutations) ─────────────────────────────────────────
interface DataContextValue extends AppState {
  // Bill mutations
  createBill: (params: { supplierId: string; linkedPoId: string | null; jobRef: string }) => string;
  updateBill: (id: string, updates: Partial<Bill>) => void;
  setBillLineItems: (billId: string, items: BillLineItem[]) => void;
  deleteBillImport: (billId: string) => void;

  // PO mutations
  createPurchaseOrder: (params: {
    supplierId: string;
    jobRef: string;
    reference?: string;
    lines?: POLineDetail[];
  }) => string;
  updatePurchaseOrder: (poId: string, updates: Partial<PurchaseOrder>) => void;
  updatePOLineDetails: (poId: string, lines: POLineDetail[]) => void;
  addPOLineDetails: (lines: POLineDetail[]) => void;

  // Catalog + supplier-code reconciliation (v1.17)
  addProduct: (product: Omit<Product, 'id'> & { id?: number }) => Product;
  addSupplierCodeMap: (map: SupplierCodeMap) => void;
  lookupSupplierCode: (supplierId: string, supplierCode: string) => string | undefined;

  // Supplier price book (v1.18)
  addSupplierPriceBookEntries: (entries: Array<Omit<SupplierPriceBookEntry, 'id' | 'createdAt'> & { id?: string; createdAt?: string }>) => { added: number; updated: number };
  setSupplierPriceBookInternalCode: (supplierId: string, supplierCode: string, internalCode: string) => void;
  lookupSupplierPriceBookEntry: (supplierId: string, supplierCode: string) => SupplierPriceBookEntry | undefined;

  // Workspace settings (v1.20)
  setTrackSupplierCodes: (on: boolean) => void;
  setInternalCodePrefix: (prefix: string) => void;
  setAutoLinkBillReference: (on: boolean) => void;
  setAccountingProduct: (p: AccountingProduct) => void;

  // Statement mutations
  addStatement: (s: Omit<Statement, 'id' | 'uploadedAt'> & { id?: string }) => string;
  updateStatement: (id: string, updates: Partial<Statement>) => void;
  setStatementStatus: (id: string, status: StatementStatus) => void;
  deleteStatement: (id: string) => void;

  // Job mutations (v1.15)
  addJob: (j: Omit<Job, 'createdAt'> & { createdAt?: string }) => string;
  addJobs: (rows: Array<Omit<Job, 'createdAt'> & { createdAt?: string }>) => { added: number; skipped: number };
  updateJob: (id: string, updates: Partial<Job>) => void;
  deleteJob: (id: string) => void;

  // Quote mutations (v1.29)
  createQuote: (params?: { customerId?: string; template?: string; ownerSalesRep?: string }) => string;
  updateQuote: (id: string, updates: Partial<Quote>) => void;
  setQuoteStatus: (id: string, status: QuoteStatus) => void;
  updateQuoteLineDetails: (quoteId: string, lines: QuoteLineDetail[]) => void;
  deleteQuote: (id: string) => void;

  // Invoice mutations (v1.30)
  createInvoice: (params?: { customerId?: string; template?: string; ownerSalesRep?: string; linkedQuoteId?: string }) => string;
  updateInvoice: (id: string, updates: Partial<Invoice>) => void;
  setInvoiceStatus: (id: string, status: InvoiceStatus) => void;
  updateInvoiceLineDetails: (invoiceId: string, lines: InvoiceLineDetail[]) => void;
  deleteInvoice: (id: string) => void;
  removeInvoicePayment: (paymentId: string) => void;

  // Data management
  resetData: () => void;
  exportData: () => string;
}

// ── Default state from seed data ──────────────────────────────────────────────
function buildDefaultState(): AppState {
  return {
    suppliers:      DEFAULT_SUPPLIERS,
    products:       DEFAULT_PRODUCTS,
    purchaseOrders: DEFAULT_PURCHASE_ORDERS,
    poLineDetails:  DEFAULT_PO_LINE_DETAILS.map(l => ({
      ...l,
      actualQty: l.actualQty ?? l.orderedQty,
    })),
    bills:          DEFAULT_BILLS,
    billLineItems:  DEFAULT_BILL_LINE_ITEMS,
    statements:     DEFAULT_STATEMENTS,
    sites:          DEFAULT_SITES,
    projects:       DEFAULT_PROJECTS,
    jobs:           DEFAULT_JOBS,
    quotes:         DEFAULT_QUOTES,
    quoteLineDetails: DEFAULT_QUOTE_LINE_DETAILS,
    invoices:       DEFAULT_INVOICES,
    invoiceLineDetails: DEFAULT_INVOICE_LINE_DETAILS,
    invoicePayments: DEFAULT_INVOICE_PAYMENTS,
    supplierCodeMaps: [],
    supplierPriceBook: [],
    // Default OFF — most orgs don't keep separate supplier stock codes.
    // Turn ON when suppliers have their own codes that need mapping on import.
    trackSupplierCodes: false,
    // FieldInsight → FIE-101, FIE-102, … (configurable in footer).
    internalCodePrefix: 'FIE',
    autoLinkBillReference: false,
    accountingProduct: 'Xero',
  };
}

// v1.21 — lineType terminology migration: 'inventory' → 'product',
// 'nonStock' → 'service'. Saved states from v1.20 carry the old values.
function migrateLineType(v?: string): 'product' | 'service' | undefined {
  if (v === 'inventory') return 'product';
  if (v === 'nonStock') return 'service';
  if (v === 'product' || v === 'service') return v;
  return undefined;
}

/** v1.50.2 — FieldInsight has four bill statuses only; fold legacy prototype values. */
function migrateBillStatus(status: string): BillStatus {
  switch (status) {
    case 'Awaiting Approval':
    case 'Pending Goods Receipt':
    case 'Disputed':
      return 'Awaiting approval';
    case 'Awaiting approval':
    case 'Draft':
    case 'Approved':
    case 'Paid':
      return status;
    default:
      return 'Draft';
  }
}

/** v1.50.3 — FieldInsight PO statuses; fold legacy prototype values. */
function migratePoStatus(status: string): PoStatus {
  switch (status) {
    case 'Partially Received':
      return 'Partial received';
    case 'Received':
      return 'All received';
    case 'Bill Received':
    case 'Bill Pending Goods':
      return 'Bill received';
    case 'Canceled':
      return 'Cancelled';
    case 'Draft':
    case 'Submitted':
    case 'Approved':
    case 'Partial received':
    case 'All received':
    case 'Bill received':
    case 'Finalised':
    case 'Cancelled':
      return status;
    default:
      return 'Draft';
  }
}

// ── Load / save to localStorage ───────────────────────────────────────────────
function loadState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaultState();
    const parsed = JSON.parse(raw) as AppState;
    if (parsed.poLineDetails) {
      parsed.poLineDetails = parsed.poLineDetails.map(l => ({
        ...l,
        lineType: migrateLineType(l.lineType as string | undefined),
        actualQty: l.actualQty ?? l.orderedQty,
      }));
    }
    if (parsed.billLineItems) {
      for (const key of Object.keys(parsed.billLineItems)) {
        parsed.billLineItems[key] = parsed.billLineItems[key].map(l => ({
          ...l, lineType: migrateLineType(l.lineType as string | undefined),
        }));
      }
    }
    if (parsed.bills) {
      parsed.bills = parsed.bills.map(b => ({
        ...b,
        status: migrateBillStatus(b.status as string),
      }));
    }
    if (parsed.purchaseOrders) {
      parsed.purchaseOrders = parsed.purchaseOrders.map(po => ({
        ...po,
        status: migratePoStatus(po.status as string),
      }));
    }
    // Merge any missing keys from defaults (for when seed data is updated)
    const defaults = buildDefaultState();
    return {
      suppliers:      parsed.suppliers      ?? defaults.suppliers,
      products:       parsed.products       ?? defaults.products,
      purchaseOrders: parsed.purchaseOrders ?? defaults.purchaseOrders,
      poLineDetails:  parsed.poLineDetails  ?? defaults.poLineDetails,
      bills:          parsed.bills          ?? defaults.bills,
      billLineItems:  parsed.billLineItems  ?? defaults.billLineItems,
      // v1.14.2 — Statement merge strategy:
      //   1. If user has none saved -> use seed (initial load case).
      //   2. Otherwise, prefer saved data BUT for any seeded statement IDs
      //      that exist in saved state with queueManaged !== true (i.e. they
      //      represent the demo / legacy statements, not user uploads),
      //      replace them with the seed version. This recovers from the
      //      v1.13 reconciler bug where seed counts were silently zeroed.
      //      User-created statements (queueManaged === true, or unknown IDs)
      //      are left untouched.
      statements: (() => {
        if (!parsed.statements || parsed.statements.length === 0) return defaults.statements;
        const seedIds = new Set(defaults.statements.map(s => s.id));
        const merged = parsed.statements.map(s => {
          if (seedIds.has(s.id) && !s.queueManaged) {
            const seed = defaults.statements.find(d => d.id === s.id)!;
            return seed;
          }
          return s;
        });
        // Re-add any seed statements that disappeared from saved state.
        for (const seed of defaults.statements) {
          if (!merged.some(m => m.id === seed.id)) merged.unshift(seed);
        }
        return merged;
      })(),
      sites:          parsed.sites          ?? defaults.sites,
      projects:       parsed.projects       ?? defaults.projects,
      jobs:           parsed.jobs           ?? defaults.jobs,
      quotes:         parsed.quotes         ?? defaults.quotes,
      quoteLineDetails: parsed.quoteLineDetails ?? defaults.quoteLineDetails,
      invoices:       parsed.invoices       ?? defaults.invoices,
      invoiceLineDetails: parsed.invoiceLineDetails ?? defaults.invoiceLineDetails,
      invoicePayments: parsed.invoicePayments ?? defaults.invoicePayments,
      supplierCodeMaps: parsed.supplierCodeMaps ?? defaults.supplierCodeMaps,
      supplierPriceBook: parsed.supplierPriceBook ?? defaults.supplierPriceBook,
      trackSupplierCodes: parsed.trackSupplierCodes ?? defaults.trackSupplierCodes,
      internalCodePrefix: (() => {
        const raw = parsed.internalCodePrefix ?? defaults.internalCodePrefix;
        const letters = String(raw).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
        return letters.length === 3 ? letters : defaults.internalCodePrefix;
      })(),
      autoLinkBillReference: parsed.autoLinkBillReference ?? defaults.autoLinkBillReference,
      accountingProduct: parsed.accountingProduct ?? defaults.accountingProduct,
    };
  } catch {
    return buildDefaultState();
  }
}

function saveState(state: AppState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage full or blocked — proceed without persistence
  }
}

// ── Context ───────────────────────────────────────────────────────────────────
const DataContext = createContext<DataContextValue | null>(null);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setStateRaw] = useState<AppState>(loadState);

  // Helper: update state and persist atomically
  const setState = useCallback((updater: (prev: AppState) => AppState) => {
    setStateRaw(prev => {
      const next = updater(prev);
      saveState(next);
      return next;
    });
  }, []);

  // ── Bill mutations ─────────────────────────────────────────────────────────

  const createBill = useCallback((params: { supplierId: string; linkedPoId: string | null; jobRef: string }): string => {
    const id = `BILL-NEW-${Date.now()}`;
    setState(prev => {
      const nextNum = prev.bills.length + 1;
      const billNo = `BILL-2026-${String(nextNum).padStart(3, '0')}`;
      const newBill: Bill = {
        id,
        billNo,
        date: new Date().toISOString().slice(0, 10),
        supplierId: params.supplierId,
        supplierInvoiceNo: '',
        linkedPoId: params.linkedPoId,
        jobRef: params.jobRef,
        reference: params.linkedPoId ?? '',
        status: 'Draft',
        pdfUploaded: false,
        processed: false,
        subtotalExGst: 0,
        totalGst: 0,
        totalIncGst: 0,
        notes: '',
      };
      return { ...prev, bills: [newBill, ...prev.bills] };
    });
    return id;
  }, [setState]);

  const updateBill = useCallback((id: string, updates: Partial<Bill>) => {
    setState(prev => ({
      ...prev,
      bills: prev.bills.map(b => b.id === id ? { ...b, ...updates } : b),
    }));
  }, [setState]);

  const setBillLineItems = useCallback((billId: string, items: BillLineItem[]) => {
    setState(prev => ({
      ...prev,
      billLineItems: { ...prev.billLineItems, [billId]: items },
    }));
  }, [setState]);

  const deleteBillImport = useCallback((billId: string) => {
    setState(prev => {
      const { [billId]: _removed, ...rest } = prev.billLineItems;
      return {
        ...prev,
        bills: prev.bills.map(b => b.id === billId
          ? { ...b, processed: false, pdfUploaded: false, supplierInvoiceNo: '', subtotalExGst: 0, totalGst: 0, totalIncGst: 0 }
          : b
        ),
        billLineItems: rest,
      };
    });
  }, [setState]);

  // ── PO mutations ───────────────────────────────────────────────────────────

  /**
   * Creates a brand-new draft PO. Used by the Bill Importer "Create PO from
   * Invoice" action when a supplier sends us a bill against no PO. Caller
   * supplies supplier + job + (optional) project ref encoded in `reference`,
   * plus the parsed invoice line items as POLineDetails.
   */
  const createPurchaseOrder = useCallback((params: {
    supplierId: string;
    jobRef: string;
    reference?: string;
    lines?: POLineDetail[];
  }): string => {
    let newId = '';
    setState(prev => {
      const seq = prev.purchaseOrders.length + 1;
      newId = `PO-NEW-${String(seq).padStart(4, '0')}`;
      const lines = (params.lines ?? []).map(l => ({ ...l, poId: newId }));
      const exGst = lines.reduce((s, l) => {
        const base = l.unitPrice * l.orderedQty;
        const disc = l.discount ? (base * l.discount) / 100 : 0;
        return s + base - disc;
      }, 0);
      const newPo: PurchaseOrder = {
        id: newId,
        date: new Date().toISOString().slice(0, 10),
        supplierId: params.supplierId,
        jobRef: params.jobRef,
        reference: params.reference ?? '',
        lines: lines.length,
        exGst: Math.round(exGst * 100) / 100,
        gst:   Math.round(exGst * 10)  / 100,
        total: Math.round(exGst * 110) / 100,
        status: 'Draft',
      };
      return {
        ...prev,
        purchaseOrders: [newPo, ...prev.purchaseOrders],
        poLineDetails: [...prev.poLineDetails, ...lines],
      };
    });
    return newId;
  }, [setState]);

  const updatePurchaseOrder = useCallback((poId: string, updates: Partial<PurchaseOrder>) => {
    setState(prev => ({
      ...prev,
      purchaseOrders: prev.purchaseOrders.map(po =>
        po.id === poId ? { ...po, ...updates } : po
      ),
    }));
  }, [setState]);

  const updatePOLineDetails = useCallback((poId: string, lines: POLineDetail[]) => {
    setState(prev => {
      // Remove existing lines for this PO and replace with new ones
      const others = prev.poLineDetails.filter(l => l.poId !== poId);
      const updatedPOs = prev.purchaseOrders.map(po => {
        if (po.id !== poId) return po;
        const exGst = lines.reduce((s, l) => {
          const base = l.unitPrice * l.orderedQty;
          const disc = l.discount ? base * l.discount / 100 : 0;
          return s + base - disc;
        }, 0);
        return { ...po, lines: lines.length, exGst: Math.round(exGst * 100) / 100, gst: Math.round(exGst * 10) / 100, total: Math.round(exGst * 110) / 100 };
      });
      return {
        ...prev,
        purchaseOrders: updatedPOs,
        poLineDetails: [...others, ...lines],
      };
    });
  }, [setState]);

  const addPOLineDetails = useCallback((lines: POLineDetail[]) => {
    setState(prev => ({
      ...prev,
      poLineDetails: [...prev.poLineDetails, ...lines],
    }));
  }, [setState]);

  // ── Catalog + supplier-code reconciliation (v1.17) ─────────────────────────

  /**
   * Adds a new product to the catalog. Used by the "Create new code" path in
   * ResolveCodeDialog when a supplier line has no matching internal code yet.
   * Returns the created product (with assigned id) so the caller can set the
   * line's internalCode immediately.
   */
  const addProduct = useCallback((product: Omit<Product, 'id'> & { id?: number }): Product => {
    const created: Product = {
      ...product,
      id: product.id ?? Date.now(),
    } as Product;
    setState(prev => ({ ...prev, products: [created, ...prev.products] }));
    return created;
  }, [setState]);

  /**
   * Persists a per-supplier supplier-code -> internal-code mapping. De-dupes on
   * (supplierId, supplierCode) — a later reconciliation overwrites an earlier
   * one. Called ONLY at reconciliation, never on raw import.
   */
  const addSupplierCodeMap = useCallback((map: SupplierCodeMap) => {
    const sc = map.supplierCode.trim();
    if (!sc || !map.internalCode.trim()) return;
    setState(prev => {
      // v1.43 — hard-off: capture nothing when supplier-code tracking is disabled.
      if (!prev.trackSupplierCodes) return prev;
      const rest = prev.supplierCodeMaps.filter(m =>
        !(m.supplierId === map.supplierId && m.supplierCode.toLowerCase() === sc.toLowerCase())
      );
      return { ...prev, supplierCodeMaps: [{ ...map, supplierCode: sc }, ...rest] };
    });
  }, [setState]);

  /** Returns the remembered internal code for (supplierId, supplierCode), if any. */
  const lookupSupplierCode = useCallback((supplierId: string, supplierCode: string): string | undefined => {
    const sc = (supplierCode ?? '').trim().toLowerCase();
    if (!sc) return undefined;
    return state.supplierCodeMaps.find(m =>
      m.supplierId === supplierId && m.supplierCode.trim().toLowerCase() === sc
    )?.internalCode;
  }, [state.supplierCodeMaps]);

  // ── Supplier price book (v1.18) ────────────────────────────────────────────

  /**
   * Bulk add/update per-supplier price-book entries, de-duped on
   * (supplierId, supplierCode). An existing entry's description/price are
   * refreshed; a non-empty internalCode is preserved if the new entry omits it.
   * Used when a supplier quote is imported (one entry per quote line).
   */
  const addSupplierPriceBookEntries = useCallback((
    entries: Array<Omit<SupplierPriceBookEntry, 'id' | 'createdAt'> & { id?: string; createdAt?: string }>
  ): { added: number; updated: number } => {
    let added = 0;
    let updated = 0;
    setState(prev => {
      // v1.43 — hard-off: no price-book capture when supplier-code tracking is disabled.
      if (!prev.trackSupplierCodes) return prev;
      const next = [...prev.supplierPriceBook];
      for (const e of entries) {
        const sc = (e.supplierCode ?? '').trim();
        if (!sc) continue;
        const idx = next.findIndex(x =>
          x.supplierId === e.supplierId && x.supplierCode.trim().toLowerCase() === sc.toLowerCase()
        );
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            description: e.description || next[idx].description,
            unitPrice: e.unitPrice || next[idx].unitPrice,
            internalCode: e.internalCode || next[idx].internalCode,
            stocked: e.stocked ?? next[idx].stocked,
          };
          updated++;
        } else {
          next.unshift({
            id: e.id ?? `SPB-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            supplierId: e.supplierId,
            supplierCode: sc,
            description: e.description,
            unitPrice: e.unitPrice,
            internalCode: e.internalCode,
            stocked: e.stocked ?? false,
            source: e.source,
            createdAt: e.createdAt ?? new Date().toISOString(),
          });
          added++;
        }
      }
      return { ...prev, supplierPriceBook: next };
    });
    return { added, updated };
  }, [setState]);

  /** Link a supplier price-book entry to an internal code (called on reconcile). */
  const setSupplierPriceBookInternalCode = useCallback((supplierId: string, supplierCode: string, internalCode: string) => {
    const sc = (supplierCode ?? '').trim().toLowerCase();
    if (!sc) return;
    setState(prev => ({
      ...prev,
      supplierPriceBook: prev.supplierPriceBook.map(x =>
        x.supplierId === supplierId && x.supplierCode.trim().toLowerCase() === sc
          ? { ...x, internalCode }
          : x
      ),
    }));
  }, [setState]);

  /** Find a supplier price-book entry by (supplierId, supplierCode). */
  const lookupSupplierPriceBookEntry = useCallback((supplierId: string, supplierCode: string): SupplierPriceBookEntry | undefined => {
    const sc = (supplierCode ?? '').trim().toLowerCase();
    if (!sc) return undefined;
    return state.supplierPriceBook.find(x =>
      x.supplierId === supplierId && x.supplierCode.trim().toLowerCase() === sc
    );
  }, [state.supplierPriceBook]);

  const setTrackSupplierCodes = useCallback((on: boolean) => {
    setState(prev => ({ ...prev, trackSupplierCodes: on }));
  }, [setState]);

  const setInternalCodePrefix = useCallback((prefix: string) => {
    const letters = String(prefix).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    if (letters.length !== 3) return;
    setState(prev => ({ ...prev, internalCodePrefix: letters }));
  }, [setState]);

  const setAccountingProduct = useCallback((p: AccountingProduct) => {
    setState(prev => ({ ...prev, accountingProduct: p }));
  }, [setState]);

  const setAutoLinkBillReference = useCallback((on: boolean) => {
    setState(prev => ({ ...prev, autoLinkBillReference: on }));
  }, [setState]);

  // ── Statement mutations ────────────────────────────────────────────────────

  const addStatement = useCallback((s: Omit<Statement, 'id' | 'uploadedAt'> & { id?: string }): string => {
    const today = new Date();
    const ymd = today.toISOString().slice(0, 10).replace(/-/g, '');
    let newId = '';
    setState(prev => {
      const seq = prev.statements.filter(x => x.id.startsWith(`STMT-${ymd}-`)).length + 1;
      newId = s.id ?? `STMT-${ymd}-${String(seq).padStart(3, '0')}`;
      const next: Statement = {
        ...s,
        id: newId,
        uploadedAt: today.toISOString(),
      };
      return { ...prev, statements: [next, ...prev.statements] };
    });
    return newId;
  }, [setState]);

  const updateStatement = useCallback((id: string, updates: Partial<Statement>) => {
    setState(prev => ({
      ...prev,
      statements: prev.statements.map(s => s.id === id ? { ...s, ...updates } : s),
    }));
  }, [setState]);

  const setStatementStatus = useCallback((id: string, status: StatementStatus) => {
    setState(prev => ({
      ...prev,
      statements: prev.statements.map(s => s.id === id ? { ...s, status } : s),
    }));
  }, [setState]);

  const deleteStatement = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      statements: prev.statements.filter(s => s.id !== id),
    }));
  }, [setState]);

  // ── Job mutations (v1.15) ──────────────────────────────────────────────────
  // Jobs are now first-class entities. `addJobs` is the bulk-import primitive
  // used by the Jobs CSV importer. It de-duplicates by jobRef (case-insens.)
  // and returns counts for the import summary.

  const addJob = useCallback((j: Omit<Job, 'createdAt'> & { createdAt?: string }): string => {
    setState(prev => {
      const next: Job = { ...j, createdAt: j.createdAt ?? new Date().toISOString() };
      return { ...prev, jobs: [next, ...prev.jobs] };
    });
    return j.id;
  }, [setState]);

  const addJobs = useCallback((rows: Array<Omit<Job, 'createdAt'> & { createdAt?: string }>): { added: number; skipped: number } => {
    let added = 0;
    let skipped = 0;
    setState(prev => {
      const seen = new Set(prev.jobs.map(x => x.jobRef.trim().toLowerCase()));
      const toAdd: Job[] = [];
      for (const r of rows) {
        const key = r.jobRef.trim().toLowerCase();
        if (!key) { skipped++; continue; }
        if (seen.has(key)) { skipped++; continue; }
        seen.add(key);
        toAdd.push({ ...r, createdAt: r.createdAt ?? new Date().toISOString() });
        added++;
      }
      return { ...prev, jobs: [...toAdd, ...prev.jobs] };
    });
    return { added, skipped };
  }, [setState]);

  const updateJob = useCallback((id: string, updates: Partial<Job>) => {
    setState(prev => ({
      ...prev,
      jobs: prev.jobs.map(j => j.id === id ? { ...j, ...updates } : j),
    }));
  }, [setState]);

  const deleteJob = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      jobs: prev.jobs.filter(j => j.id !== id),
    }));
  }, [setState]);

  // ── Quote mutations (v1.29) ────────────────────────────────────────────────
  // Sell-side quotes. Mirrors the PO mutation surface: create spawns a Draft
  // quote with the next sequential number; updateQuoteLineDetails replaces the
  // quote's lines and recomputes the rollups (lines/exGst/gst/total).

  const recomputeQuoteRollups = (lines: QuoteLineDetail[]) => {
    const exGst = lines.reduce((s, l) => {
      const base = l.salesPrice * l.qty;
      const disc = l.discount ? (base * l.discount) / 100 : 0;
      return s + base - disc;
    }, 0);
    const gst = lines.filter(l => l.taxable).reduce((s, l) => {
      const base = l.salesPrice * l.qty;
      const disc = l.discount ? (base * l.discount) / 100 : 0;
      return s + (base - disc) * 0.1;
    }, 0);
    return {
      lines: lines.length,
      exGst: Math.round(exGst * 100) / 100,
      gst: Math.round(gst * 100) / 100,
      total: Math.round((exGst + gst) * 100) / 100,
    };
  };

  const createQuote = useCallback((params?: { customerId?: string; template?: string; ownerSalesRep?: string }): string => {
    let newId = '';
    setState(prev => {
      const nums = prev.quotes
        .map(q => parseInt(q.number.replace(/[^0-9]/g, ''), 10))
        .filter(n => !isNaN(n));
      const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 401;
      const number = `QUO-${String(nextNum).padStart(4, '0')}`;
      newId = `${number}-1`;
      const newQuote: Quote = {
        id: newId, number, revision: 1,
        status: 'Draft', template: params?.template ?? 'Default Template',
        customerId: params?.customerId,
        quoteDate: new Date().toISOString().slice(0, 10),
        ownerSalesRep: params?.ownerSalesRep ?? 'Paul Tyrrell',
        businessEntity: 'Northbridge Diesel & Mechanical',
        division: 'Service Div',
        purchasesTax: 'Exclusive', salesTax: 'Exclusive',
        createdBy: params?.ownerSalesRep ?? 'Paul Tyrrell',
        createdAt: new Date().toISOString(),
        lines: 0, exGst: 0, gst: 0, total: 0,
      };
      return { ...prev, quotes: [newQuote, ...prev.quotes] };
    });
    return newId;
  }, [setState]);

  const updateQuote = useCallback((id: string, updates: Partial<Quote>) => {
    setState(prev => ({
      ...prev,
      quotes: prev.quotes.map(q => q.id === id ? { ...q, ...updates } : q),
    }));
  }, [setState]);

  const setQuoteStatus = useCallback((id: string, status: QuoteStatus) => {
    setState(prev => ({
      ...prev,
      quotes: prev.quotes.map(q => q.id === id ? { ...q, status } : q),
    }));
  }, [setState]);

  const updateQuoteLineDetails = useCallback((quoteId: string, lines: QuoteLineDetail[]) => {
    setState(prev => {
      const others = prev.quoteLineDetails.filter(l => l.quoteId !== quoteId);
      const scoped = lines.map(l => ({ ...l, quoteId }));
      const rollups = recomputeQuoteRollups(scoped);
      return {
        ...prev,
        quoteLineDetails: [...others, ...scoped],
        quotes: prev.quotes.map(q => q.id === quoteId ? { ...q, ...rollups } : q),
      };
    });
  }, [setState]);

  const deleteQuote = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      quotes: prev.quotes.filter(q => q.id !== id),
      quoteLineDetails: prev.quoteLineDetails.filter(l => l.quoteId !== id),
    }));
  }, [setState]);

  // ── Invoice mutations (v1.30) ──────────────────────────────────────────────

  const recomputeInvoiceRollups = (lines: InvoiceLineDetail[]) => {
    const exGst = lines.reduce((s, l) => {
      const base = l.salesPrice * l.qty;
      const disc = l.discount ? (base * l.discount) / 100 : 0;
      return s + base - disc;
    }, 0);
    const gst = lines.filter(l => l.taxable).reduce((s, l) => {
      const base = l.salesPrice * l.qty;
      const disc = l.discount ? (base * l.discount) / 100 : 0;
      return s + (base - disc) * 0.1;
    }, 0);
    const total = Math.round((exGst + gst) * 100) / 100;
    return {
      lines: lines.length,
      exGst: Math.round(exGst * 100) / 100,
      gst: Math.round(gst * 100) / 100,
      total,
      owingAmount: total,
    };
  };

  const createInvoice = useCallback((params?: { customerId?: string; template?: string; ownerSalesRep?: string; linkedQuoteId?: string }): string => {
    let newId = '';
    setState(prev => {
      const nums = prev.invoices
        .map(i => parseInt(i.id.replace(/[^0-9]/g, ''), 10))
        .filter(n => !isNaN(n));
      const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 108;
      newId = `INV-${String(nextNum).padStart(4, '0')}`;
      const today = new Date().toISOString().slice(0, 10);
      const due = new Date();
      due.setDate(due.getDate() + 14);
      const newInvoice: Invoice = {
        id: newId, status: 'Draft', template: params?.template ?? 'Original',
        customerId: params?.customerId,
        linkedQuoteId: params?.linkedQuoteId,
        invoiceDate: today, invoiceTerm: '14 Day Terms',
        dueDays: 14, dueDate: due.toISOString().slice(0, 10),
        ownerSalesRep: params?.ownerSalesRep ?? 'Paul Tyrrell',
        businessEntity: 'Northbridge Diesel & Mechanical',
        purchasesTax: 'Exclusive', salesTax: 'Exclusive',
        createdBy: params?.ownerSalesRep ?? 'Paul Tyrrell',
        createdAt: new Date().toISOString(),
        lines: 0, exGst: 0, gst: 0, total: 0, paidAmount: 0, owingAmount: 0,
      };
      return { ...prev, invoices: [newInvoice, ...prev.invoices] };
    });
    return newId;
  }, [setState]);

  const updateInvoice = useCallback((id: string, updates: Partial<Invoice>) => {
    setState(prev => ({
      ...prev,
      invoices: prev.invoices.map(i => i.id === id ? { ...i, ...updates } : i),
    }));
  }, [setState]);

  const setInvoiceStatus = useCallback((id: string, status: InvoiceStatus) => {
    setState(prev => ({
      ...prev,
      invoices: prev.invoices.map(i => i.id === id ? { ...i, status } : i),
    }));
  }, [setState]);

  const updateInvoiceLineDetails = useCallback((invoiceId: string, lines: InvoiceLineDetail[]) => {
    setState(prev => {
      const scoped = lines.map(l => ({ ...l, invoiceId }));
      const others = prev.invoiceLineDetails.filter(l => l.invoiceId !== invoiceId);
      const rollups = recomputeInvoiceRollups(scoped);
      const inv = prev.invoices.find(i => i.id === invoiceId);
      const paid = inv?.paidAmount ?? 0;
      return {
        ...prev,
        invoiceLineDetails: [...others, ...scoped],
        invoices: prev.invoices.map(i => i.id === invoiceId
          ? { ...i, ...rollups, owingAmount: Math.max(0, rollups.total - paid) }
          : i),
      };
    });
  }, [setState]);

  const deleteInvoice = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      invoices: prev.invoices.filter(i => i.id !== id),
      invoiceLineDetails: prev.invoiceLineDetails.filter(l => l.invoiceId !== id),
      invoicePayments: prev.invoicePayments.filter(p => p.invoiceId !== id),
    }));
  }, [setState]);

  const syncInvoicePaidTotals = (
    invoices: Invoice[],
    invoiceId: string,
    payments: InvoicePayment[],
  ): Invoice[] => invoices.map(inv => {
    if (inv.id !== invoiceId) return inv;
    const paid = payments
      .filter(p => p.invoiceId === invoiceId)
      .reduce((s, p) => s + p.amountPaid, 0);
    const paidRounded = Math.round(paid * 100) / 100;
    return {
      ...inv,
      paidAmount: paidRounded,
      owingAmount: Math.max(0, Math.round((inv.total - paidRounded) * 100) / 100),
    };
  });

  const removeInvoicePayment = useCallback((paymentId: string) => {
    setState(prev => {
      const target = prev.invoicePayments.find(p => p.id === paymentId);
      if (!target) return prev;
      const invoicePayments = prev.invoicePayments.filter(p => p.id !== paymentId);
      return {
        ...prev,
        invoicePayments,
        invoices: syncInvoicePaidTotals(prev.invoices, target.invoiceId, invoicePayments),
      };
    });
  }, [setState]);

  // ── Data management ────────────────────────────────────────────────────────

  const resetData = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  }, []);

  const exportData = useCallback((): string => {
    return JSON.stringify(state, null, 2);
  }, [state]);

  const value: DataContextValue = {
    ...state,
    createBill,
    updateBill,
    setBillLineItems,
    deleteBillImport,
    createPurchaseOrder,
    updatePurchaseOrder,
    updatePOLineDetails,
    addPOLineDetails,
    addProduct,
    addSupplierCodeMap,
    lookupSupplierCode,
    addSupplierPriceBookEntries,
    setSupplierPriceBookInternalCode,
    lookupSupplierPriceBookEntry,
    setTrackSupplierCodes,
    setInternalCodePrefix,
    setAutoLinkBillReference,
    setAccountingProduct,
    addStatement,
    updateStatement,
    setStatementStatus,
    deleteStatement,
    addJob,
    addJobs,
    updateJob,
    deleteJob,
    createQuote,
    updateQuote,
    setQuoteStatus,
    updateQuoteLineDetails,
    deleteQuote,
    createInvoice,
    updateInvoice,
    setInvoiceStatus,
    updateInvoiceLineDetails,
    deleteInvoice,
    removeInvoicePayment,
    resetData,
    exportData,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export function useData(): DataContextValue {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside <DataProvider>');
  return ctx;
}
